<template>
  <HrPageLayout
    title="Disciplinary & HR Actions"
    subtitle="Warnings, show cause, suspension, commendation, promotion, transfer – all appear in employee HR history"
    icon="⚖️"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Public Service Board Module' }, { label: 'Disciplinary & HR Action' }]"
  >
    <div class="director-module">
      <div class="filters-row">
        <select v-model="filters.action_type" @change="loadActions">
          <option value="">All action types</option>
          <option v-for="t in actionTypes" :key="t" :value="t">{{ t }}</option>
        </select>
        <select v-model="filters.employeeId" @change="loadActions">
          <option value="">All employees</option>
          <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.firstName }} {{ e.lastName }} ({{ e.empNo }})</option>
        </select>
        <input v-model="filters.fromDate" type="date" @change="loadActions">
        <input v-model="filters.toDate" type="date" @change="loadActions">
        <button type="button" class="hr-btn hr-btn-primary" @click="openActionModal()">Add HR Action</button>
        <button type="button" class="hr-btn hr-btn-secondary" @click="openTimelineModal()">View employee timeline</button>
      </div>
      <div class="table-wrap card-section">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Action type</th>
              <th>Date</th>
              <th>Description</th>
              <th>Document</th>
              <th>Issued by</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in actions" :key="a.id">
              <td>{{ a.firstName }} {{ a.lastName }}<br><small>{{ a.empNo }}</small></td>
              <td>{{ a.action_type }}</td>
              <td>{{ a.action_date }}</td>
              <td>{{ (a.description || '').slice(0, 60) }}{{ (a.description || '').length > 60 ? '…' : '' }}</td>
              <td>
                <button v-if="a.document_attachment" type="button" class="hr-btn hr-btn-sm hr-btn-info" @click="viewAttachment(a)">View PDF</button>
                <span v-else>—</span>
              </td>
              <td>{{ a.issued_by || '–' }}</td>
              <td>{{ a.status }}</td>
              <td>
                <button type="button" class="hr-btn hr-btn-sm" @click="editAction(a)">Edit</button>
                <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" @click="showEmployeeTimeline(a.employee_id)">Timeline</button>
              </td>
            </tr>
            <tr v-if="!actions.length"><td colspan="8" class="text-muted">No actions</td></tr>
          </tbody>
        </table>
      </div>

      <div v-if="showActionModal" class="modal-overlay" @click.self="showActionModal = false">
        <div class="modal-card">
          <h3>{{ editingId ? 'Edit' : 'Add' }} HR action</h3>
          <div class="modal-form-grid">
          <div class="form-group">
            <label>Employee</label>
            <select v-model="form.employee_id" required :disabled="!!editingId">
              <option value="">Select</option>
              <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.firstName }} {{ e.lastName }} ({{ e.empNo }})</option>
            </select>
          </div>
          <div class="form-group">
            <label>Action type</label>
            <select v-model="form.action_type" required>
              <option value="">Select</option>
              <option v-for="t in actionTypes" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Action date</label>
            <input v-model="form.action_date" type="date" required>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="form.description" rows="3"></textarea>
          </div>
          <div class="form-group">
            <label>Issued by</label>
            <input v-model="form.issued_by" type="text">
          </div>
          <div class="form-group">
            <label>PDF attachment</label>
            <input ref="pdfInput" type="file" accept=".pdf,application/pdf" @change="onPdfSelected">
            <small class="text-muted">PDF only.</small>
            <div v-if="form.document_attachment" class="mt-1">
              Current: <button type="button" class="hr-btn hr-btn-sm hr-btn-info" @click="viewAttachment(form)">View current PDF</button>
            </div>
          </div>
          <div class="form-group">
            <label>Status</label>
            <select v-model="form.status">
              <option value="Active">Active</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showActionModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveAction">Save</button>
          </div>
        </div>
      </div>

      <div v-if="showTimelineModal" class="modal-overlay" @click.self="showTimelineModal = false">
        <div class="modal-card modal-card-wide">
          <h3>HR history timeline – {{ timelineEmployeeName }}</h3>
          <div class="form-group">
            <label>Select employee</label>
            <select v-model="timelineEmployeeId" @change="loadEmployeeTimeline">
              <option value="">Select</option>
              <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.firstName }} {{ e.lastName }} ({{ e.empNo }})</option>
            </select>
          </div>
          <div class="timeline-list">
            <div v-for="item in timelineActions" :key="item.id" class="timeline-item">
              <div class="timeline-marker" :class="getActionClass(item.action_type)"></div>
              <div class="timeline-content">
                <strong>{{ item.action_type }}</strong> – {{ item.action_date }}
                <div v-if="item.description" class="timeline-desc">{{ item.description }}</div>
                <div class="timeline-meta">Issued by: {{ item.issued_by || '–' }} · {{ item.created_at }}</div>
              </div>
            </div>
            <div v-if="timelineEmployeeId && !timelineActions.length" class="text-muted">No HR actions for this employee.</div>
          </div>
          <div class="modal-actions">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showTimelineModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, executeDownload, authUrl, toast } from "@/api";

const ACTION_TYPES = ['Warning', 'Show Cause Letter', 'Suspension', 'Interdiction', 'Termination', 'Commendation', 'Promotion', 'Transfer'];

export default {
  name: "DisciplinaryHrActions",
  components: { HrPageLayout },
  data() {
    return {
      actionTypes: ACTION_TYPES,
      filters: {
        action_type: '',
        employeeId: '',
        fromDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
        toDate: new Date().toISOString().slice(0, 10)
      },
      actions: [],
      employees: [],
      showActionModal: false,
      showTimelineModal: false,
      editingId: null,
      saving: false,
      timelineEmployeeId: '',
      timelineActions: [],
      form: {
        id: null,
        employee_id: '',
        action_type: '',
        action_date: new Date().toISOString().slice(0, 10),
        description: '',
        issued_by: '',
        document_attachment: '',
        status: 'Active'
      },
      selectedPdfFile: null
    };
  },
  computed: {
    timelineEmployeeName() {
      if (!this.timelineEmployeeId) return '';
      const e = this.employees.find((x) => x.id == this.timelineEmployeeId);
      return e ? `${e.firstName} ${e.lastName} (${e.empNo})` : '';
    }
  },
  created() {
    this.loadEmployees();
    this.loadActions();
  },
  methods: {
    loadActions() {
      const fd = new FormData();
      fd.append('function', 'getHrActions');
      if (this.filters.employeeId) fd.append('employeeId', this.filters.employeeId);
      if (this.filters.action_type) fd.append('action_type', this.filters.action_type);
      if (this.filters.fromDate) fd.append('fromDate', this.filters.fromDate);
      if (this.filters.toDate) fd.append('toDate', this.filters.toDate);
      execute(fd, authUrl).then((res) => {
        this.actions = (res.data && res.data.success && res.data.data && res.data.data.actions) ? res.data.data.actions : [];
      }).catch(() => { this.actions = []; });
    },
    loadEmployees() {
      const fd = new FormData();
      fd.append('function', 'getEmployees');
      fd.append('page', '1');
      fd.append('limit', '500');
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.employees) {
          this.employees = res.data.data.employees;
        }
      }).catch(() => {});
    },
    loadEmployeeTimeline() {
      if (!this.timelineEmployeeId) {
        this.timelineActions = [];
        return;
      }
      const fd = new FormData();
      fd.append('function', 'getEmployeeHrActions');
      fd.append('employeeId', this.timelineEmployeeId);
      execute(fd, authUrl).then((res) => {
        this.timelineActions = (res.data && res.data.success && res.data.data && res.data.data.actions) ? res.data.data.actions : [];
      }).catch(() => { this.timelineActions = []; });
    },
    openActionModal(record) {
      this.editingId = record ? record.id : null;
      this.selectedPdfFile = null;
      if (record) {
        this.form = {
          id: record.id,
          employee_id: record.employee_id,
          action_type: record.action_type,
          action_date: (record.action_date || '').slice(0, 10),
          description: record.description,
          issued_by: record.issued_by,
          document_attachment: record.document_attachment,
          status: record.status || 'Active'
        };
      } else {
        this.form = {
          id: null,
          employee_id: '',
          action_type: '',
          action_date: new Date().toISOString().slice(0, 10),
          description: '',
          issued_by: '',
          document_attachment: '',
          status: 'Active'
        };
      }
      this.$nextTick(() => {
        if (this.$refs.pdfInput) this.$refs.pdfInput.value = "";
      });
      this.showActionModal = true;
    },
    editAction(a) {
      this.openActionModal(a);
    },
    onPdfSelected(e) {
      const file = e?.target?.files?.[0] || null;
      if (!file) {
        this.selectedPdfFile = null;
        return;
      }
      const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name || '');
      if (!isPdf) {
        this.selectedPdfFile = null;
        if (this.$refs.pdfInput) this.$refs.pdfInput.value = '';
        toast.error('Only PDF is allowed');
        return;
      }
      this.selectedPdfFile = file;
    },
    saveAction() {
      if (!this.form.employee_id || !this.form.action_type || !this.form.action_date) return;
      this.saving = true;
      const fd = new FormData();
      fd.append('function', 'saveHrAction');
      Object.keys(this.form).forEach((k) => fd.append(k, this.form[k] == null ? '' : this.form[k]));
      if (this.selectedPdfFile) fd.append('document_file', this.selectedPdfFile);
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showActionModal = false;
          this.loadActions();
          if (this.showTimelineModal && this.timelineEmployeeId == this.form.employee_id) {
            this.loadEmployeeTimeline();
          }
        } else toast.error(res.data && res.data.message ? res.data.message : 'Save failed');
      }).catch(() => { this.saving = false; });
    },
    async viewAttachment(a) {
      const id = a?.id || this.form?.id;
      if (!id) return;
      try {
        const data = new FormData();
        data.append('function', 'downloadHrActionAttachment');
        data.append('id', String(id));
        const response = await executeDownload(data, authUrl);
        const blob = response?.data;
        if (!(blob instanceof Blob)) {
          toast.error('Could not open attachment');
          return;
        }
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        toast.error('Could not open attachment');
      }
    },
    openTimelineModal() {
      this.timelineEmployeeId = this.filters.employeeId || '';
      this.showTimelineModal = true;
      this.loadEmployeeTimeline();
    },
    showEmployeeTimeline(employeeId) {
      this.timelineEmployeeId = String(employeeId);
      this.showTimelineModal = true;
      this.loadEmployeeTimeline();
    },
    getActionClass(type) {
      const t = (type || '').toLowerCase();
      if (t.includes('warning') || t.includes('show cause') || t.includes('suspension') || t.includes('interdiction') || t.includes('termination')) return 'action-disciplinary';
      if (t.includes('commendation') || t.includes('promotion') || t.includes('transfer')) return 'action-positive';
      return 'action-neutral';
    }
  }
};
</script>

<style scoped>
.director-module { padding: 0 0 24px; }
.filters-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px; }
.card-section { background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); padding: 16px; }
.table-wrap { overflow-x: auto; }
.table { width: 100%; font-size: 13px; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 24px; max-width: 520px; width: 90%; max-height: 90vh; overflow-y: auto; }
.modal-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.modal-card-wide { max-width: 640px; }
.form-group { margin-bottom: 12px; }
.form-group label { display: block; font-weight: 600; margin-bottom: 4px; }
.form-group textarea { min-height: 84px; }
.mt-1 { margin-top: 4px; }
.modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
.timeline-list { margin: 16px 0; padding-left: 24px; border-left: 2px solid #dee2e6; }
.timeline-item { position: relative; margin-bottom: 16px; }
.timeline-item .timeline-marker { position: absolute; left: -30px; top: 4px; width: 12px; height: 12px; border-radius: 50%; }
.timeline-item .timeline-marker.action-disciplinary { background: #dc3545; }
.timeline-item .timeline-marker.action-positive { background: #28a745; }
.timeline-item .timeline-marker.action-neutral { background: #6c757d; }
.timeline-content { font-size: 14px; }
.timeline-desc { margin-top: 4px; color: #495057; }
.timeline-meta { font-size: 12px; color: #6c757d; margin-top: 4px; }
.text-muted { color: #6c757d; }
</style>
